"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { getAllPortfolioList } from "@/lib/ApiClient";
import { getPortfolioProject } from "@/lib/Utility";
import SuccessPortfolios from "./SuccessPortfolios";
import { PortfolioAllListInterface } from "@/lib/Types";

const PAGE_LIMIT = 6;

interface PortfolioProps {
  initialPortfolios?: PortfolioAllListInterface[];
}

export default function Portfolio({ initialPortfolios = [] }: PortfolioProps) {
  const hasInitial = initialPortfolios.length > 0;
  const initialHasMore = hasInitial
    ? initialPortfolios.length >= PAGE_LIMIT
    : true;

  const [portfolioArr, setPortfolioArr] =
    useState<PortfolioAllListInterface[]>(initialPortfolios);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(initialHasMore);

  // ✅ useRef for mutable values (no re-render trigger)
  const pageRef = useRef(hasInitial ? 2 : 1);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(initialHasMore);

  const fetchPortfolios = useCallback(async () => {
    // 🚫 Prevent duplicate / parallel calls
    if (loadingRef.current || !hasMoreRef.current) return;

    loadingRef.current = true;
    setLoading(true);

    try {
      const data = await getAllPortfolioList(pageRef.current, PAGE_LIMIT);
      const unifiedArr = getPortfolioProject(data ?? []);

      // ✅ Append safely
      setPortfolioArr((prev) => [...prev, ...unifiedArr]);

      // ✅ Pagination control
      if (unifiedArr.length < PAGE_LIMIT) {
        hasMoreRef.current = false;
        setHasMore(false);
      } else {
        pageRef.current += 1;
      }
    } catch (err) {
      console.error("Portfolio fetch error:", err);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  // ✅ First batch is server-rendered; only fetch on mount as a fallback
  // when no initial data was provided (e.g. server fetch failed).
  useEffect(() => {
    if (!hasInitial) fetchPortfolios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SuccessPortfolios
      portfolioArr={portfolioArr}
      listLoading={loading}
      hasMore={hasMore}
      loadMore={fetchPortfolios}
    />
  );
}
